import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '@/App';
import { getExam, submitExam } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Clock, ArrowLeft, ArrowRight, CheckCircle, AlertTriangle } from 'lucide-react';

export default function TakeExam() {
  const { examId } = useParams();
  const { currentStudent } = useAppContext();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentQ, setCurrentQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const timerRef = useRef(null);
  const studentId = currentStudent?.id;

  useEffect(() => {
    let cancelled = false;

    const loadExam = async () => {
      try {
        setLoading(true);
        if (timerRef.current) clearInterval(timerRef.current);
        const res = await getExam(examId);
        if (cancelled) return;
        setExam(res.data);
        setTimeLeft(res.data.duration_minutes * 60);
        timerRef.current = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load exam');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadExam();

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [examId]);

  // Auto-submit when timer runs out
  useEffect(() => {
    if (timeLeft !== 0 || !exam || result || !studentId || submitting) return;

    const autoSubmit = async () => {
      try {
        setSubmitting(true);
        setShowConfirm(false);
        if (timerRef.current) clearInterval(timerRef.current);
        const res = await submitExam({
          exam_id: examId,
          answers
        });
        setResult(res.data);
        toast.success('Exam submitted successfully!');
      } catch (err) {
        toast.error('Failed to submit exam');
      } finally {
        setSubmitting(false);
      }
    };

    autoSubmit();
  }, [timeLeft, exam, result, studentId, submitting, examId, answers]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setShowConfirm(false);
      if (timerRef.current) clearInterval(timerRef.current);
      const res = await submitExam({
        student_id: studentId,
        exam_id: examId,
        answers
      });
      setResult(res.data);
      toast.success('Exam submitted successfully!');
    } catch (err) {
      toast.error('Failed to submit exam');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) return <div className="h-96 bg-white rounded-xl animate-pulse" />;
  if (!exam) return <div className="text-center py-16 text-[#062B1A]/50">Exam not found</div>;

  const questions = exam.questions || [];
  const currentQuestion = questions[currentQ];
  const answeredCount = Object.keys(answers).length;
  // Back to the course this quiz belongs to. The old /student/exams list no
  // longer exists and redirects to My Courses, which is not where the student
  // was working.
  const backToCourse = () => navigate(
    exam.course_id ? `/student/courses/${exam.course_id}` : '/student/courses',
  );

  // Show result screen
  if (result) {
    return (
      <div className="max-w-lg mx-auto text-center py-12 animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-[#062B1A] mb-2" style={{ fontFamily: 'var(--font-display)' }}>Exam Completed!</h2>
        <p className="text-[#062B1A]/60 mb-6">{exam.title}</p>
        <div className="bg-white rounded-xl border border-[rgba(6, 43, 26,0.08)] p-6 mb-6">
          <p className="text-4xl font-bold text-[#D4AF37] mb-1">{result.score}/{result.max_score}</p>
          <p className="text-sm text-[#062B1A]/50">Your Score</p>
          <div className="mt-4 h-2 bg-[#062B1A]/10 rounded-full overflow-hidden">
            <div className="h-full bg-[#D4AF37] rounded-full" style={{ width: `${(result.score / result.max_score) * 100}%` }} />
          </div>
          <p className="text-xs text-[#062B1A]/40 mt-2">{Math.round((result.score / result.max_score) * 100)}% correct</p>
        </div>
        <Button className="bg-[#062B1A] text-white hover:bg-[#041B10]" onClick={backToCourse}>Back to course</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Exam Header Bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-[rgba(6, 43, 26,0.08)] p-4 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" aria-label="Back to course" onClick={backToCourse} className="text-[#062B1A]/60">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h3 className="text-sm font-semibold text-[#062B1A]">{exam.title}</h3>
            <p className="text-xs text-[#062B1A]/40">Question {currentQ + 1} of {questions.length}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={`flex items-center gap-1.5 px-3 py-1 ${
            timeLeft < 60 ? 'bg-red-100 text-red-700' : 'bg-[#062B1A] text-white'
          }`} data-testid="exam-timer">
            <Clock className="w-3.5 h-3.5" />
            {formatTime(timeLeft)}
          </Badge>
          <Badge variant="outline" className="text-xs">{answeredCount}/{questions.length} answered</Badge>
          <Button size="sm" className="bg-[#D4AF37] text-black hover:bg-[#B8941F] btn-hover" onClick={() => setShowConfirm(true)} data-testid="exam-submit-button">
            Submit Exam
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Question Area */}
        <div className="lg:col-span-8">
          {currentQuestion && (
            <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Badge className="bg-[#062B1A]/5 text-[#062B1A] border-0 text-xs">Q{currentQ + 1}</Badge>
                  <Badge variant="outline" className="text-[10px] text-[#D4AF37] border-[#D4AF37]/30">{currentQuestion.marks} marks</Badge>
                </div>
                <p className="text-base font-medium text-[#062B1A] mb-6">{currentQuestion.text}</p>
                {/* A short answer is typed; every other type is chosen from the
                    options the server sent, which for true/false is the pair it
                    supplies rather than anything stored on the question. */}
                {currentQuestion.type === 'short_answer' ? (
                  <div>
                    <Label htmlFor={`q${currentQ}-answer`} className="mb-2 block text-sm text-[#062B1A]/70">
                      Your answer
                    </Label>
                    <Textarea
                      id={`q${currentQ}-answer`}
                      value={answers[String(currentQ)] ?? ''}
                      onChange={(e) => setAnswers(prev => {
                        const next = { ...prev };
                        // An emptied box is unanswered, not an answer of "".
                        if (e.target.value.trim()) next[String(currentQ)] = e.target.value;
                        else delete next[String(currentQ)];
                        return next;
                      })}
                      placeholder="Type your answer"
                      className="min-h-[110px]"
                      data-testid="short-answer-input"
                    />
                  </div>
                ) : (
                  <RadioGroup
                    value={answers[String(currentQ)]?.toString() || ''}
                    onValueChange={(val) => setAnswers(prev => ({ ...prev, [String(currentQ)]: parseInt(val) }))}
                    className="space-y-3"
                  >
                    {currentQuestion.options?.map((opt, idx) => (
                      <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                        answers[String(currentQ)] === idx
                          ? 'border-[#D4AF37]/50 bg-[#D4AF37]/5'
                          : 'border-[rgba(6, 43, 26,0.08)] hover:bg-[#F7F8FA]'
                      }`}>
                        <RadioGroupItem value={String(idx)} id={`q${currentQ}-opt${idx}`} />
                        <Label htmlFor={`q${currentQ}-opt${idx}`} className="flex-1 cursor-pointer text-sm text-[#062B1A]">{opt}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
                <div className="flex items-center justify-between mt-6">
                  <Button variant="outline" disabled={currentQ === 0} onClick={() => setCurrentQ(prev => prev - 1)} className="border-[rgba(6, 43, 26,0.12)]">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  <Button disabled={currentQ === questions.length - 1} onClick={() => setCurrentQ(prev => prev + 1)} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
                    Next <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Question Navigator */}
        <div className="lg:col-span-4">
          <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
            <CardContent className="p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50 mb-3">Questions</p>
              <div className="grid grid-cols-5 gap-2">
                {questions.map((_, idx) => {
                  const isAnswered = answers[String(idx)] !== undefined;
                  const isCurrent = currentQ === idx;
                  return (
                    <button
                      key={idx}
                      onClick={() => setCurrentQ(idx)}
                      className={`w-9 h-9 rounded-lg text-xs font-medium border transition-colors ${
                        isCurrent
                          ? 'question-current border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#062B1A]'
                          : isAnswered
                            ? 'question-answered border-[#062B1A]/20 bg-[#062B1A]/5 text-[#062B1A]'
                            : 'question-unanswered border-[rgba(6, 43, 26,0.12)] text-[#062B1A]/40'
                      }`}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 space-y-1.5">
                <div className="flex items-center gap-2 text-[10px] text-[#062B1A]/40">
                  <div className="w-3 h-3 rounded border border-[#D4AF37]/60 bg-[#D4AF37]/10" /> Current
                </div>
                <div className="flex items-center gap-2 text-[10px] text-[#062B1A]/40">
                  <div className="w-3 h-3 rounded border border-[#062B1A]/20 bg-[#062B1A]/5" /> Answered
                </div>
                <div className="flex items-center gap-2 text-[10px] text-[#062B1A]/40">
                  <div className="w-3 h-3 rounded border border-[rgba(6, 43, 26,0.12)]" /> Unanswered
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Confirm Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#D4AF37]" /> Submit Exam?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#062B1A]/60">
            You have answered {answeredCount} out of {questions.length} questions.
            {answeredCount < questions.length && ' Some questions are unanswered.'}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Confirm Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
